<template>

  <div>

    <!-- Rate settings constants -->
    <!-- XOL-497: badge and caption share ONE live region with aria-atomic, so
         a screen reader hears "Published. v3 is the current version…" as a
         single utterance rather than two disconnected fragments. Because the
         loading state renders nothing, this region is empty until the value
         settles and the result is announced exactly once. -->
    <div class="rate-settings__heading" role="status" aria-live="polite" aria-atomic="true">
      <h4 class="rate-settings__title">Rate settings</h4>

      <el-tag
        v-if="publishState === 'published'"
        size="mini"
        type="success"
        effect="dark">
        <i class="fa fa-check" aria-hidden="true"></i> Published
      </el-tag>

      <el-tag
        v-else-if="publishState === 'unpublished'"
        size="mini"
        type="warning">
        <i class="fa fa-exclamation-triangle" aria-hidden="true"></i> Never published
      </el-tag>

      <el-tag
        v-else-if="publishState === 'unsaved'"
        size="mini"
        type="info">
        <i class="fa fa-file" aria-hidden="true"></i> Not published yet
      </el-tag>

      <!-- Grey, not red: the RATE is not broken, our lookup is. A red badge on
           a money screen sends someone hunting a data problem that isn't
           there. The question-mark icon carries the uncertainty so colour is
           never the only signal. -->
      <el-tag
        v-else-if="publishState === 'unknown'"
        size="mini"
        type="info">
        <i class="fa fa-question-circle" aria-hidden="true"></i> Publish state unknown
      </el-tag>

      <!-- INSIDE the live region deliberately. An earlier revision had this as
           a sibling, which made the aria-atomic comment above false: only the
           tag was announced, and the caption — which carries the actual
           consequence — was never read. It is a flex item of the heading row,
           so flex-basis: 100% puts it on its own line. -->
      <p class="rate-settings__publish-caption">{{ publishCaption }}</p>
    </div>

    <!-- Info banner -->
    <el-alert
      type="info"
      :closable="false"
      show-icon
      style="margin-bottom: 16px;">
      <template #default>
        These apply to every tier below. Tier-specific values (Tenor Range, Rate #1–#N, MW Rate, RRU Rate, RRU Set) live inside each tier card.
      </template>
    </el-alert>

    <el-form inline label-position="top">

      <select-sharer label="Sharer" v-model="row.organisation_id" :disabled="disabled" :error="sharerError" class="rate-settings__named-field" />
      <text-input label="Name" v-model="row.name" :disabled="disabled" :error="nameError" class="rate-settings__named-field" />
      <!-- XOL-487: "First Sharer Only" checkbox removed — 0 of 71 rates had it
           set. The API is dropping sharer_rates.firstSharerOnly and
           rate_versions.firstSharerOnly separately. agreements.firstSharerOnly
           is a DIFFERENT column on a DIFFERENT table and is NOT being
           dropped — AgreementDetails.vue's switch stays. Do not delete or
           reset row.firstSharerOnly anywhere on this write path: the key
           must keep riding through prepareRow()/normalizeHeader() untouched
           so a legacy value isn't silently rewritten on the next save. -->

      <!-- XOL-319: single mutually-exclusive choice replaces the two independent
           "Rate By Sharer No / Qty" toggles (which allowed ambiguous both-on/both-off). -->
      <el-form-item>
        <template #label><span>Rate billing <help-tip :content="rateBySharerHelp" /></span></template>
        <el-radio-group v-model="sharerRateMode" :disabled="disabled">
          <el-radio-button label="none">None</el-radio-button>
          <el-radio-button label="no">By Sharer No</el-radio-button>
          <el-radio-button label="qty">By Sharer Qty</el-radio-button>
        </el-radio-group>
      </el-form-item>

      <!-- XOL-462: strict billing rules — own visual group so the two switches
           don't wrap orphaned beside the radio group above. -->
      <div class="rate-settings__strict">
        <div class="rate-settings__strict-title">Strict billing rules</div>

        <el-form-item>
          <template #label><span>Strict term <help-tip :content="strictTermHelp" /></span></template>
          <el-switch
            v-model="row.strictTerm"
            :disabled="disabled"
            active-text="On"
            inactive-text="Off"
            inactive-color="#DCDFE6"
            aria-label="Strict term" />
        </el-form-item>

        <el-form-item>
          <template #label><span>{{ strictPositionLabel }} <help-tip :content="strictPositionHelp" /></span></template>
          <el-switch
            v-model="row.strictPosition"
            :disabled="disabled"
            active-text="On"
            inactive-text="Off"
            inactive-color="#DCDFE6"
            :aria-label="strictPositionLabel" />
        </el-form-item>
      </div>

      <el-row :gutter="guts">

        <!-- XOL-486: Min is unconditional — it applies to every sharer rate,
             strict or lenient (Carlos, 2026-09-08: "Min and Max show for both
             strict and non-strict"). This reverses XOL-464's v-if, which hid
             the field when Strict position (now upper-bound-only) was off. -->
        <el-col :xs="24" :sm="24" :md="8" :lg="6">
          <el-form-item>
            <template #label><span>{{ fromSharerLabel }} <help-tip :content="fromSharerHelp" /></span></template>
            <!-- XOL-498: @change, not @input — number-input relays
                 el-input-number's @change, which commits on blur/Enter/step
                 rather than per keystroke. Coupling on keystrokes would fight
                 the user mid-type (typing "1" on the way to "10"). -->
            <number-input no-form :min="1" :max="10" v-model="row.fromSharer" :disabled="disabled" @change="onMinCommitted" />
          </el-form-item>
          <div v-if="fromSharerError" style="color:#f56c6c; font-size:12px; margin-top:4px;">{{ fromSharerError }}</div>
          <div v-else-if="bandFollowNote && bandFollowNote.field === 'from'" class="band-follow-note">{{ bandFollowNote.text }}</div>
        </el-col>

        <el-col :xs="24" :sm="24" :md="8" :lg="6">
          <number-input :label="toSharerLabel" :min="1" :max="10" v-model="row.toSharer" :disabled="disabled" @change="onMaxCommitted" />
          <div v-if="toSharerError" style="color:#f56c6c; font-size:12px; margin-top:4px;">{{ toSharerError }}</div>
          <div v-else-if="bandFollowNote && bandFollowNote.field === 'to'" class="band-follow-note">{{ bandFollowNote.text }}</div>
        </el-col>

        <el-col :xs="24" :sm="24" :md="8" :lg="6">
          <number-input label="Height (metres)" :min="0" :max="121" v-model="row.height" :disabled="disabled" />
        </el-col>

      </el-row>

    </el-form>

    <!-- XOL-497 v3: the Min-change alert. ONE alert, not three.
         Placed OUTSIDE </el-form> on purpose. `.el-form--inline` styles
         descendants via `.el-form--inline .el-form-item`, so anything nested
         inside picks up inline-form layout it was never designed for — the
         trap that bit XOL-451. Sitting here it also follows the Min field in
         DOM and tab order, and appearing does not shove the whole form down.

         THE PRODUCT RULE THIS ENCODES (Carlos, 2026-09-13): every licensee is
         pinned to a rate version, and a licensee moves only through a manual
         or bulk edit. There is no "unpinned"/"follows the live rate"
         population, so no copy here may describe one.

         THREE REVISIONS WERE WRONG BEFORE THIS ONE. Do not reintroduce any of
         them:
           v1 "pinned licensees keep the old Min UNTIL YOU PUBLISH A NEW ONE"
              — publishing does not move a pinned licensee.
              `RateVersionPublisher` writes no pin; `licensees.rate_version_id`
              is set only at attach and by PUT /licensees/{id}/rate-version.
           v2 "the new Min applies AS SOON AS YOU SAVE" — `currentRate` is a
              stored column that a plain Save never rewrites.
           v2 "...reaches licensees FOLLOWING THE CURRENT RATE" — describes a
              population the product does not allow.

         Re-pinning can only target a version that already belongs to this rate
         (SharerController rejects any other with a 422), so after a plain Save
         every available version still carries the OLD Min. That is why the
         instruction is Save + Publish FIRST, then re-pin — telling the user to
         re-pin on its own sends them in a circle.

         "Bulk licensee rate change" IS named here now, and the ban that used
         to sit in this comment is lifted. It was correct when written: the
         bulk tool could not re-pin a licensee already on this rate, which is
         every licensee this alert is about. Both halves of that are fixed on
         API `main` (merged 2026-09-14, a6f7780), and both were re-read before
         this copy changed rather than taken from the ticket:

           - Checkbox selection PUTs one row at a time to /licensees/{id}.
             SharerController now has a `$pinOnlyChange` branch (XOL-511) that
             honours a version-only change, provided the key is PRESENT and
             NON-NULL. BulkRateAssign's `requestForRow` sends it exactly then
             (`if (self.versionId) payload.rate_version_id = …`), so the two
             line up.
           - Filter mode goes to the bulk endpoint. BulkRateApplier's
             idempotent skip was keyed on rate_id alone and swallowed every
             version change; XOL-510 added the pin to that test, so a
             same-rate, different-pin row now applies instead of skipping.

         If either of those is reverted, this copy becomes false again — it
         promises an action the user would otherwise watch silently do nothing.

         The bulk clause is gated on isSuper(); the Finance-tab clause is
         unconditional because XOL-516 made that tab's gate a superset of this
         alert's audience. See minAlertBody for the gate table.

         Naming a route the reader cannot take is the defect this alert shipped
         twice — first the bulk tool (isSuper-only) to every finance manager,
         then the Finance tab to management/admin managers who were locked out
         of it. The second was fixed by moving the GATE, not the copy.

         The Save + Publish FIRST ordering is unchanged and still load-bearing:
         re-pinning can only target a version that already belongs to this rate,
         so after a plain Save every available version still carries the OLD
         Min.

         el-alert's root already carries role="alert", so it is not repeated
         here. Safe rather than chatty because NumberInput commits on
         el-input-number's @change (blur / Enter / step), not per keystroke. -->
    <el-alert
      v-if="showMinAlert"
      type="warning"
      :closable="false"
      show-icon
      title="Existing licensees keep their current Min"
      style="margin-top: 12px;">
      <template #default>{{ minAlertBody }}</template>
    </el-alert>

    <hr style="border: 0; border-top: 1px solid #ebeef5; margin: 16px 0;" />

    <!-- Tier editor -->
    <sharer-rate-tiers
      ref="tiersEditor"
      v-model="row.tiers"
      :disabled="disabled"
      :to-sharer="row.toSharer"
      :from-sharer="effectiveFromSharer"
      :validation-errors="tierValidationErrors">
    </sharer-rate-tiers>

  </div>

</template>



<script>

import axios from '@/lib/axiosClient'

import NumberMixins from '@/mixins/NumberMixins'
import PostMixins from '@/mixins/PostMixins'

import SelectSharer from '@/components/atoms/selects/SelectSharer'
import SharerRateTiers from './tiers/SharerRateTiers'

export default {

  mixins: [NumberMixins, PostMixins],

  components: {
    SelectSharer,
    SharerRateTiers
  },

  props: {
    rowData: {
      type: Object,
      default: null
    }
  },

  computed: {
    // XOL-497 v4: the Min alert's body. A computed rather than static slot text
    // because the recovery routes a user actually HAS depend on their access.
    //
    // TWO audiences. The gates, as they now stand:
    //
    //   alert audience   isManager('finance|management|admin')             (disabled())
    //   Finance tab      isManager('finance|project|management|admin')     (FinanceSharer:40, canSwitchVersion)
    //   bulk tool        isSuper()                                         (FinanceSettings:108)
    //
    // The first two ARE nested now — the tab's set strictly contains the
    // alert's — so the tab clause is unconditional and only the bulk clause
    // branches.
    //
    // They were NOT nested until XOL-516 (2026-09-15). The tab was
    // finance|project, so a management/admin manager could edit Min, see this
    // alert, and reach neither route; a third branch stated the consequence
    // and stopped. XOL-516 widened the tab's gate instead, which killed that
    // branch. `canAccess` is a plain membership test on userRole, so all of
    // this is decided by role strings, not hierarchy.
    minAlertBody () {
      var fact = 'Licensees keep the version they\'re pinned to — saving or publishing doesn\'t move them.'

      // TWO branches, not three — and the reason is recorded because a third
      // one lived here briefly.
      //
      // The Finance tab is gated `isManager('finance|project|management|admin')
      // && !readOnly` (FinanceSharer, XOL-516). That set is a superset of this
      // alert's own audience, `isManager('finance|management|admin')`, so every
      // reader of this alert can reach the tab and the clause is unconditional.
      //
      // It was NOT a superset until XOL-516: the tab was finance|project, so a
      // management/admin manager saw this alert and could reach neither route,
      // and a third branch stated the consequence and stopped. XOL-516 widened
      // the gate instead (Carlos, 2026-09-15), which made that branch dead and
      // it was deleted with its tests.
      //
      // `readOnly` is not mirrored because it is never bound —
      // SiteFinanceSharers.vue:11 is the sole instantiation of finance-sharer
      // and passes only :sharer and :site-data. If anyone binds :read-only,
      // this reasoning breaks; that is the thing to grep for.
      if (this.isSuper()) {
        return fact + ' To give them this Min, Save + Publish, then re-pin: ' +
          'one licensee on its sharer\'s Finance tab, or several at once with Bulk licensee rate change.'
      }

      return fact + ' To give them this Min, Save + Publish, then re-pin ' +
        'each licensee on its sharer\'s Finance tab.'
    },

    disabled () {
      return !this.isManager('finance|management|admin')
    },

    // XOL-497 Step 7 — has Min moved since the drawer opened?
    //
    // This is a DIRTY CHECK rather than a diff against the published value —
    // a CHOICE, not a constraint. Since XOL-500 (API c0bcf804) the versions
    // payload DOES carry fromSharer/toSharer per entry, so a real diff is
    // possible. It buys nothing here: the v3 copy states a rule that holds
    // whether or not Min effectively moved, so the extra comparison would add
    // a branch and change no output. An earlier revision of this comment
    // asserted the payload lacked those fields and called the dirty check
    // "forced by the API"; that stopped being true on 2026-09-09.
    //
    // Consequence, accepted deliberately: changing Min from 2 to 3 and back to
    // 2 still reports changed. Over-warning on a money screen is the safe
    // direction — the failure it guards against is a user excluding sharers,
    // seeing a clean save, and being billed for them anyway. Any copy shown
    // for this state has to stay true when the value was restored.
    //
    // Guarded on `initialFromSharer !== null` so it cannot fire during the
    // window before created() sets the baseline.
    minChanged () {
      if (this.initialFromSharer === null) return false
      return Number(this.row.fromSharer) !== this.initialFromSharer
    },

    // XOL-497 — the ONE place that decides which publish state applies.
    // Everything rendered keys off this string, so no template v-if can
    // drift out of agreement with publishAdvice().
    //
    // Order is the whole design. `hasPublishedVersion === false` is true in
    // four different situations, only one of which means "never published",
    // so the disqualifying cases are checked FIRST:
    //
    //   'loading'     fetch in flight — renders NOTHING (see below)
    //   'unknown'     fetch failed — we know nothing, and say so
    //   'unsaved'     no row.id: create or duplicate, no request was made
    //   'published'   confirmed current version exists
    //   'unpublished' confirmed saved rate with no current version
    //
    // A naive `v-if="!hasPublishedVersion"` would print "Never published" on
    // a rate whose lookup merely failed — a factual claim about billing we
    // cannot back, and the mirror of the rule that a failed fetch must never
    // read as "Published".
    // On the 'unknown' vs 'unsaved' ordering specifically: today it cannot
    // matter, because the no-id path sets publishErrored = false and returns
    // without a request, so (errored && no id) is unreachable — swapping these
    // two lines is an equivalent mutation and no test can distinguish it. It
    // is written errored-first anyway, because that ordering stays correct if
    // the no-id branch ever stops clearing the flag, and the failure it would
    // otherwise produce is the one this whole computed exists to prevent.
    publishState () {
      if (!this.publishLoaded) return 'loading'
      if (this.publishErrored) return 'unknown'
      if (!this.row || !this.row.id) return 'unsaved'
      return this.hasPublishedVersion ? 'published' : 'unpublished'
    },

    // XOL-497 v3 — show the Min alert, or not. One boolean, because there is
    // now one alert; v2 had a three-way discriminator for three alerts.
    //
    // Silent unless Min actually moved, and silent while loading. Shown on
    // 'published' AND on 'unknown': the message asserts nothing about the
    // publish state, only that pinned licensees do not move, so it stays true
    // when the lookup failed. Silent on 'unpublished'/'unsaved' — a rate with
    // no published version cannot be attached to a licensee at all (XOL-484),
    // so there is nobody to strand.
    //
    // No count. v2 named `pinned_count`, which was wrong twice over: it counts
    // terminated licensees, and after an edit-and-revert it claimed licensees
    // "will not get this Min" when their pinned version already had it. The
    // rule statement needs no number, which is also why the SPA does not need
    // the API's proposed `pinned_active_count` (XOL-506).
    showMinAlert () {
      if (!this.minChanged) return false
      return this.publishState === 'published' || this.publishState === 'unknown'
    },

    // Muted caption under the badge. Empty string for 'loading' because that
    // state renders nothing at all: a "Checking…" chip on a dense money
    // header buys nothing, costs a layout shift, and produces a
    // warning->success colour flip that reads as the rate itself changing
    // state. It is also the better a11y outcome — the live region stays
    // empty until the value settles, so the result is announced exactly once
    // instead of as "Checking… Published".
    publishCaption () {
      switch (this.publishState) {
        case 'published':
          // Naming the live version is what stops this badge contradicting
          // the Manage-versions drawer, which shows the same rate as
          // "Current draft (unpublished)" alongside its published versions.
          // Both sentences are true; saying which version is current makes
          // them read as one fact rather than two contradicting ones.
          return this.publishedVersionNo
            ? 'v' + this.publishedVersionNo + ' is the current version — new attachments and re-pins get it. Saved edits don\'t change v' + this.publishedVersionNo + '; Save + Publish makes them a new version.'
            : 'This rate has a published version — new attachments and re-pins get it. Saved edits don\'t change it; Save + Publish makes them a new version.'
        case 'unpublished':
          return 'No published version yet, so this rate can\'t be attached to a licensee. Use Save + Publish below.'
        case 'unsaved':
          // Deliberately does not name the plain "Save" button: the two
          // publishOnly instances in SharerRateOverride.vue render only
          // "Save + Publish", so naming Save would be false there. One
          // string stays true across all five mount points.
          return 'This rate isn\'t saved yet. Use Save + Publish to make it available to licensees straight away.'
        case 'unknown':
          return 'We couldn\'t check whether this rate has been published. Reopen this drawer to retry, or open Manage versions.'
        default:
          return ''
      }
    },

    // XOL-319: bridge the single radio choice to the two underlying booleans.
    // Exactly one of ratesBySharerNo / ratesBySharerQty can be true (or neither),
    // so both-on / both-off ambiguity is no longer expressible.
    sharerRateMode: {
      get () {
        if (this.row.ratesBySharerNo) return 'no'
        if (this.row.ratesBySharerQty) return 'qty'
        return 'none'
      },
      set (val) {
        this.row.ratesBySharerNo = (val === 'no')
        this.row.ratesBySharerQty = (val === 'qty')
      }
    },

    // XOL-376/XOL-462: label the shared unit per the active mode. The
    // payload keys stay toSharer/fromSharer regardless of the displayed
    // label. Neutral 'Sharers' for the unflagged mode (settled with the API
    // agent) — None falls back to the byNo-like billing path server-side,
    // but 'Sharer Count' would wrongly imply the mode is set.
    sharerUnitLabel () {
      if (this.row.ratesBySharerNo) return 'Sharer Count'
      if (this.row.ratesBySharerQty) return 'Sharer Qty'
      return 'Sharers'
    },

    // XOL-486: Min is now permanently on screen (Carlos has ruled: Min and
    // Max show for both strict and non-strict), so the 'Max ' prefix is
    // always warranted — there is always a lower bound to contrast against.
    toSharerLabel () {
      return 'Max ' + this.sharerUnitLabel
    },

    fromSharerLabel () {
      return 'Min ' + this.sharerUnitLabel
    },

    // XOL-486: single source of truth for "which sub-Min positions are hidden
    // and out of scope" — feeds both the tier-completeness loop (validateTiers,
    // below) and the tier editor's rendered Rate #N range (threaded down as
    // :from-sharer). fromSharer is meaningful on every rate now (Min is no
    // longer strictPosition-gated), so the only fallbacks left are the two
    // transient-value guards: an out-of-range fromSharer, or fromSharer >
    // toSharer. These are load-bearing for usability, not just defence — NOT
    // because number-input emits on every keystroke (it doesn't; el-input-number
    // commits via @change, on blur/Enter or the step buttons), but because a
    // COMMITTED inverted band persists: a user who commits Min = 5 while Max
    // is still 4 sits inverted indefinitely until they fix Max. Without the
    // fallback the tier grid would empty for that entire window.
    //
    // Safety invariant (pinned by a test): every state in which this fallback
    // engages is also a state in which the save is blocked — either the
    // unconditional header validation below sets fromSharerError, or (when
    // tiers is empty/absent) validateTiers's earlier return already fails the
    // save outright. The fallback is render-time only and can never leak a
    // wrong band into a saved payload.
    //
    // That earlier `if (!tiers || tiers.length === 0) return` (validateTiers,
    // below) sits ABOVE where sharerError/nameError get (re)computed too —
    // XOL-487 added those two fields alongside fromSharerError, with the same
    // exposure: in that empty-tiers state the user would see "Fix the
    // highlighted errors before saving." with sharerError/nameError left at
    // whatever they were (possibly null/stale), nothing newly highlighted.
    // Unreachable today for the same reason as above: normalizeTiers() always
    // seeds Tier 1, and SharerRateTiers.onRemove refuses to drop below one
    // tier, so row.tiers is never actually empty by the time this runs.
    effectiveFromSharer () {
      var f = Number(this.row.fromSharer)
      if (isNaN(f) || f < 1 || f > 10) return 1
      var t = Number(this.row.toSharer)
      if (!isNaN(t) && t >= 1 && f > t) return 1
      return f
    },

    // XOL-464: fixed, mode-independent label — replaces the old three-branch
    // 'Strict No' / 'Strict Qty' / 'Strict Position' computed (XOL-462 §3.2).
    // The flag governs both the No and Qty axis, so a mode-driven label
    // wrongly implied it applied to only one (Carlos, 2026-08-26 — see the
    // Decisions log). Kept as a one-line computed (not a data field) so it
    // stays alongside the other label computeds it replaced, even though it
    // no longer branches on `row`.
    strictPositionLabel () {
      return 'Strict max position'
    },

    // XOL-462 §6.2 — exact tip copy, one string across all three modes.
    rateBySharerHelp () {
      return 'Picks what decides which Rate # column a sharer is billed from — By Sharer No uses the sharer\'s own number, By Sharer Qty uses how many sharers are on the site. None leaves the rate unflagged and billing falls back to the sharer\'s number.'
    },

    // XOL-464: added a clause per the XOL-463 author (PR #242) — strict term
    // is enforced against "has the term actually lapsed", not the projection
    // horizon, so flipping it on mid-term produces no visible change on the
    // fee schedule/timeline until the term ends. Without warning for that, a
    // user who toggles it on mid-term and sees nothing change has no way to
    // tell whether it saved, took effect, or is broken.
    strictTermHelp () {
      return 'Off, the last tier\'s rate keeps billing indefinitely after that tier ends. On, billing stops at the end of the last tier and nothing is charged after that — those months still appear on the finance screens, flagged as billing errors. If the term hasn\'t ended yet, turning this on won\'t change anything you see right away — the effect only shows once the term is reached.'
    },

    // XOL-487: strictPosition now governs the UPPER bound only. Min applies
    // unconditionally regardless of this switch (see fromSharerHelp below).
    strictPositionHelp () {
      return 'This controls the top of the range only. Off, a sharer ranked above Max is still billed, at the Max position\'s rate. On, a sharer above Max is not billed at all and is flagged as a billing error. Min is not affected either way — a sharer ranked below Min is never billed, whatever this is set to.'
    },

    // XOL-487: Min help tip — explains the unconditional lower-bound rule
    // added by XOL-486, rendered on the fromSharer field's label.
    fromSharerHelp () {
      return 'The lowest billing position this rate prices. A sharer ranked below Min is not billed at all and is flagged as a billing-position error — it is not moved up to the first position. This applies on every sharer rate, whether or not Strict max position is on. Leave it at 1 if the rate should price every position.'
    }
  },

  methods: {
    // XOL-497 Step 8 — fetch whether this rate has a current published
    // version. Public: RatesAction re-calls it on Popup's `open` event so the
    // badge cannot go stale after the user publishes from the Manage-versions
    // drawer and comes back (Popup does not use destroy-on-close, so this
    // instance survives a close/reopen).
    //
    // Mirrors RateVersionManager.load() deliberately, including the raw axios:
    // fetchData() would raise a global "Unable to fetch…" toast on failure,
    // which is wrong for a passive badge the user did not ask for. A failed
    // load degrades to a quiet uncertain state instead.
    // RETURNS its promise, unlike RateVersionManager.load() which it otherwise
    // mirrors. Not cosmetic: five tests in RateVersionManager.spec.js gate
    // their assertions on `if (p && typeof p.then === 'function')` against a
    // method that returns undefined, so the branch never runs and each test
    // passes having executed ZERO expectations (XOL-493 item 5). Returning the
    // promise is what lets this method's tests actually await it. Callers may
    // ignore the return value; created() does.
    loadPublishState () {
      var self = this

      // No id means create or duplicate — there is nothing to have published,
      // and no endpoint to ask. Settle the state without a request.
      if (!this.row || !this.row.id) {
        this.publishLoading = false
        this.publishErrored = false
        this.publishLoaded = true
        this.hasPublishedVersion = false
        this.publishedVersionNo = null
        return Promise.resolve()
      }

      this.publishLoading = true
      this.publishErrored = false
      this.publishLoaded = false

      // getEndpoint() is resolved BEFORE the chain, so a throw here would
      // escape created() and strand publishLoading true — the badge would sit
      // in 'loading' forever and never settle. It degrades to silence rather
      // than a false claim, so the invariant holds either way, but muteness is
      // not the same as honesty: route it into the errored state instead.
      var url
      try {
        url = this.getEndpoint('/rates/' + this.row.id + '/versions')
      } catch (_e) {
        this.publishErrored = true
        this.hasPublishedVersion = false
        this.publishedVersionNo = null
        this.publishLoading = false
        this.publishLoaded = true
        return Promise.resolve()
      }

      return axios.get(url)
        .then(function (response) {
          var body = response ? response.data : null
          var data = (body && body.data) ? body.data : body
          var list = Array.isArray(data) ? data : []
          var current = null
          for (var i = 0; i < list.length; i++) {
            if (list[i] && list[i].is_current) { current = list[i]; break }
          }
          self.hasPublishedVersion = !!current
          // Read the same way RateVersionManager.currentVersionNo() does.
          self.publishedVersionNo = current ? current.version_no : null
          self.publishLoading = false
          self.publishLoaded = true
        })
        .catch(function (_err) {
          // Reset to false rather than merely leaving it. On the FIRST fetch
          // it is already false, so this looks redundant — it is not, because
          // Step 9 re-runs this method on every re-open. Without the reset, a
          // sequence of "open (published), close, reopen, fetch fails" leaves
          // hasPublishedVersion true: a stale positive claim outliving the
          // evidence for it. Callers that read the flag without also checking
          // publishErrored would then assert "published" on a lookup that
          // failed — the exact thing this feature must never do.
          //
          // A test covers the refetch case specifically; it caught this.
          self.publishErrored = true
          self.hasPublishedVersion = false
          self.publishedVersionNo = null
          self.publishLoading = false
          self.publishLoaded = true
        })
    },

    // XOL-497 Step 8/10 — what the PARENT should say after a successful save.
    // State lives here, messaging lives in RatesAction: the same split the
    // existing validateTiers()/setTierErrors() pair already uses.
    //
    // Both flags are false while loading or after a failed fetch, so an
    // unknown publish state produces silence rather than a guess.
    publishAdvice () {
      var known = this.publishLoaded && !this.publishErrored
      return {
        needsPublish: known && !this.hasPublishedVersion,
        minChanged: known && this.hasPublishedVersion && this.minChanged
      }
    },

    // XOL-497 v2 — re-arm the Min baseline. Called by RatesAction after a
    // successful Save + Publish ONLY.
    //
    // Without it the drawer keeps warning about a Min that was just published:
    // Popup has no destroy-on-close, so the instance and its created()-time
    // baseline survive a close/reopen, and at SharerRateOverride's "Modify"
    // mount the rate id does not change either, so nothing ever remounts. The
    // result was an alert and a toast that were simply false.
    //
    // Deliberately NOT called after a plain Save: there the draft legitimately
    // still differs from the published version, which is exactly what the
    // warning is for.
    captureMinBaseline () {
      this.initialFromSharer = Number(this.row.fromSharer)
    },

    // XOL-498: keep Min <= Max at input time, by MOVING the other field rather
    // than refusing the keystroke.
    //
    // The alternative — binding Min's :max to the current Max — is simpler and
    // equally airtight, and was rejected: with Max at 4, a user who wants
    // Min 5 / Max 8 types 5, watches it snap back to 4, and gets no reason why.
    // Following preserves the intent instead of fighting it.
    //
    // The cost is that this writes a field the user did not touch, so it MUST
    // be visible. bandFollowNote renders under whichever field moved; a silent
    // mutation of a billing band is worse than the inverted state it prevents.
    //
    // This does NOT replace validateTiers()'s save-time check (XOL-486) or the
    // API's 422 (XOL-463). It makes both unreachable through this form, which
    // is the point — effectiveFromSharer's comment above describes the window
    // this closes: a COMMITTED inverted band used to persist, emptying the tier
    // grid, until the user thought to fix the other field.
    onMinCommitted (value) {
      this.bandFollowNote = null
      var f = Number(value)
      var t = Number(this.row.toSharer)
      // Only act on a valid pair. An out-of-range value is the existing
      // validation's business, and moving the other field off a value we
      // already know is bad would compound the error.
      if (isNaN(f) || f < 1 || f > 10) return
      if (isNaN(t) || t < 1 || t > 10) return
      if (f <= t) return
      this.row.toSharer = f
      this.clearBandErrors()
      this.bandFollowNote = {
        field: 'to',
        text: 'Raised to ' + f + ' to match ' + this.fromSharerLabel + '.'
      }
    },

    onMaxCommitted (value) {
      this.bandFollowNote = null
      var t = Number(value)
      var f = Number(this.row.fromSharer)
      if (isNaN(t) || t < 1 || t > 10) return
      if (isNaN(f) || f < 1 || f > 10) return
      if (f <= t) return
      this.row.fromSharer = t
      this.clearBandErrors()
      this.bandFollowNote = {
        field: 'from',
        text: 'Lowered to ' + t + ' to match ' + this.toSharerLabel + '.'
      }
    },

    // XOL-498, found in review: the cue renders in a `v-else-if` behind the
    // field's own error, and `fromSharerError` / `toSharerError` are recomputed
    // ONLY inside validateTiers() — i.e. on a save attempt. So a stale error
    // from an earlier blocked save outlives the value it described, and would
    // win the v-else-if: the guard moves the field and the user sees an old red
    // line instead of the cue. That is precisely the silent mutation this whole
    // mechanism exists to prevent.
    //
    // Cleared only on the path where the guard actually MOVED something, not on
    // every commit. After a move both values are known valid and in order, so
    // any surviving band error is definitionally stale. An out-of-range commit
    // returns before this and keeps its error, which is still true and still
    // the user's to fix.
    clearBandErrors () {
      this.fromSharerError = null
      this.toSharerError = null
    },

    // XOL-493 taught this lesson on fieldErrors: the drawer is NOT destroyed
    // between opens, so anything written on one rate renders on the next. A
    // "Raised to 5 to match Min" note sitting under an untouched rate's Max is
    // exactly that bug. Relayed from RatesAction.onOpen().
    clearBandFollowNote () {
      this.bandFollowNote = null
    },

    setTierErrors (errors) {
      this.tierValidationErrors = errors
    },

    // XOL-462 §2.2: `row` is seeded from getTableStruct('rates') — the LEGACY
    // `rates` table, not `sharer_rates`. `strictTerm` / `strictPosition` are
    // absent entirely on a new-rate object, so a bare assignment would bind to
    // nothing (Vue 2 non-reactive-add trap): the switches would render
    // permanently off and un-clickable, with no error anywhere. $set all four
    // header keys — harmless for fromSharer/toSharer, which the legacy table
    // already carries, and load-bearing for the two strict flags. Must run
    // BEFORE normalizeTiers() / anything else reads these keys.
    normalizeHeader () {
      var r = this.row
      this.$set(r, 'strictPosition', !!r.strictPosition)
      this.$set(r, 'fromSharer', Number(r.fromSharer) > 0 ? Number(r.fromSharer) : 1)
      this.$set(r, 'toSharer', Number(r.toSharer) > 0 ? Number(r.toSharer) : 10)
      this.$set(r, 'strictTerm', !!r.strictTerm)
    },

    normalizeTiers () {
      if (this.row.tiers && Array.isArray(this.row.tiers) && this.row.tiers.length > 0) {
        // Tiers present — coerce numbers, sort by seqNo, re-stamp seqNo
        var normalized = this.row.tiers
          .slice()
          .sort(function (a, b) { return (a.seqNo || 0) - (b.seqNo || 0) })
          .map(function (t, i) {
            return {
              seqNo: i + 1,
              fromMonth: Number(t.fromMonth) || 0,
              toMonth: Number(t.toMonth) || 0,
              telco1: Number(t.telco1) || 0,
              telco2: Number(t.telco2) || 0,
              telco3: Number(t.telco3) || 0,
              telco4: Number(t.telco4) || 0,
              telco5: Number(t.telco5) || 0,
              telco6: Number(t.telco6) || 0,
              telco7: Number(t.telco7) || 0,
              telco8: Number(t.telco8) || 0,
              telco9: Number(t.telco9) || 0,
              telco10: Number(t.telco10) || 0,
              mwRate: Number(t.mwRate) || 0,
              rruRate: Number(t.rruRate) || 0,
              rruSet: Number(t.rruSet) || 0
            }
          })
        this.$set(this.row, 'tiers', normalized)
      } else if (this.row.id) {
        // Existing row with flat columns — synthesize single Tier 1
        var flatTier = {
          seqNo: 1,
          fromMonth: Number(this.row.fromMonth) || 0,
          toMonth: Number(this.row.toMonth) || 0,
          telco1: Number(this.row.telco1) || 0,
          telco2: Number(this.row.telco2) || 0,
          telco3: Number(this.row.telco3) || 0,
          telco4: Number(this.row.telco4) || 0,
          telco5: Number(this.row.telco5) || 0,
          telco6: Number(this.row.telco6) || 0,
          telco7: Number(this.row.telco7) || 0,
          telco8: Number(this.row.telco8) || 0,
          telco9: Number(this.row.telco9) || 0,
          telco10: Number(this.row.telco10) || 0,
          mwRate: Number(this.row.mwRate) || 0,
          rruRate: Number(this.row.rruRate) || 0,
          rruSet: Number(this.row.rruSet) || 0
        }
        this.$set(this.row, 'tiers', [flatTier])
      } else {
        // New record — one blank Tier 1 at month 0
        var blankTier = {
          seqNo: 1,
          fromMonth: 0,
          toMonth: 12,
          telco1: 0,
          telco2: 0,
          telco3: 0,
          telco4: 0,
          telco5: 0,
          telco6: 0,
          telco7: 0,
          telco8: 0,
          telco9: 0,
          telco10: 0,
          mwRate: 0,
          rruRate: 0,
          rruSet: 0
        }
        this.$set(this.row, 'tiers', [blankTier])
      }
    },

    // Called in created() for new records only: try to pre-fill tiers from
    // the tenant's Default Tier Template. Silent on failure — the pristine
    // blank Tier 1 is already set by normalizeTiers().
    onTemplateSeed (data) {
      // Only seed if we still have the pristine single all-zero Tier 1
      // (guard against clobbering any edits or existing rows).
      if (this.row.id) return
      if (!this.row.tiers || this.row.tiers.length !== 1) return
      var t = this.row.tiers[0]
      var isPristine = (
        t.seqNo === 1 &&
        t.fromMonth === 0 &&
        t.toMonth === 12 &&
        t.telco1 === 0 && t.telco2 === 0 && t.telco3 === 0 && t.telco4 === 0 &&
        t.telco5 === 0 && t.telco6 === 0 && t.telco7 === 0 && t.telco8 === 0 &&
        t.telco9 === 0 && t.telco10 === 0 &&
        t.mwRate === 0 && t.rruRate === 0 && t.rruSet === 0
      )
      if (!isPristine) return

      if (!data || !data.tiers || !Array.isArray(data.tiers) || data.tiers.length === 0) {
        // data:null — no template saved; keep the pristine blank Tier 1
        return
      }

      // Map template tiers into the edit dialog: null → 0 (non-template edit
      // path represents blank as 0; keeps seeded dialog consistent with a
      // manually-created rate and avoids nullable rates in PR-A tests).
      var seeded = data.tiers
        .slice()
        .sort(function (a, b) { return (a.seqNo || 0) - (b.seqNo || 0) })
        .map(function (t, i) {
          return {
            seqNo: i + 1,
            fromMonth: Number(t.fromMonth) || 0,
            toMonth: Number(t.toMonth) || 0,
            telco1: Number(t.telco1) || 0,
            telco2: Number(t.telco2) || 0,
            telco3: Number(t.telco3) || 0,
            telco4: Number(t.telco4) || 0,
            telco5: Number(t.telco5) || 0,
            telco6: Number(t.telco6) || 0,
            telco7: Number(t.telco7) || 0,
            telco8: Number(t.telco8) || 0,
            telco9: Number(t.telco9) || 0,
            telco10: Number(t.telco10) || 0,
            mwRate: Number(t.mwRate) || 0,
            rruRate: Number(t.rruRate) || 0,
            rruSet: Number(t.rruSet) || 0
          }
        })
      this.$set(this.row, 'tiers', seeded)
    },

    // Called by RatesAction before save to validate tiers.
    // Returns { valid, errors } where errors is an array of per-tier error objects.
    // Delegates the boolean to the child SharerRateTiers.validate() when mounted,
    // so the authoritative isValid computed lives in one place.
    validateTiers () {
      var tiers = this.row.tiers
      var errors = []

      // Sits above sharerError/nameError/fromSharerError below — see the
      // effectiveFromSharer comment above for the exposure and why it's
      // unreachable today.
      if (!tiers || tiers.length === 0) {
        return { valid: false, errors: errors }
      }

      // XOL-376 (R1): every tier must have Rate #1..toSharer > 0. Compute the
      // completeness limit once from row.toSharer.
      var n = Number(this.row.toSharer)
      var toSharerValid = !isNaN(n) && n >= 1 && n <= 10
      this.toSharerError = toSharerValid ? null : this.toSharerLabel + ' must be between 1 and 10.'
      var limit = toSharerValid ? Math.min(n, 10) : 0

      // XOL-486: fromSharer (Min) range 1..10 and fromSharer <= toSharer are
      // enforced on EVERY sharer rate, strict or lenient — Min binds
      // unconditionally now, so a save can no longer skip validating it.
      var f = Number(this.row.fromSharer)
      var fromSharerValid = true
      this.fromSharerError = null
      if (this.row.fromSharer === null || typeof this.row.fromSharer === 'undefined' || this.row.fromSharer === '') {
        fromSharerValid = false
        this.fromSharerError = this.fromSharerLabel + ' is required. Enter 1 if this rate prices every position.'
      } else if (isNaN(f) || f < 1 || f > 10) {
        fromSharerValid = false
        this.fromSharerError = this.fromSharerLabel + ' must be between 1 and 10.'
      } else if (toSharerValid && f > n) {
        fromSharerValid = false
        this.fromSharerError = this.fromSharerLabel + ' cannot be greater than ' + this.toSharerLabel + '.'
      }

      // XOL-487: Sharer + Name are required on every sharer rate — the client
      // side of "check required fields first" (the other side is
      // keepOpenUntilSaved, in Popup.vue/RatesAction.vue). `!row.organisation_id`
      // catches null/undefined/''/0 in one check — 0 is not a real org id.
      this.sharerError = null
      if (!this.row.organisation_id) {
        this.sharerError = 'Sharer is required.'
      }
      var sharerValid = !this.sharerError

      // Validate the TRIMMED value but do not mutate row.name — a
      // whitespace-only Name is "required", not "too long", and silently
      // trimming the user's live input would be surprising.
      var trimmedName = (this.row.name || '').toString().trim()
      this.nameError = null
      if (!trimmedName) {
        this.nameError = 'Name is required.'
      } else if (trimmedName.length > 48) {
        this.nameError = 'Name must be 48 characters or fewer.'
      }
      var nameValid = !this.nameError

      // XOL-462 §2.3: must be ANDed into BOTH branches below, including the
      // $refs.tiersEditor branch — the branch the real app takes, and one the
      // child SharerRateTiers knows nothing about (its prop contract is
      // deliberately not widened; the parent owns header errors).
      var headerValid = toSharerValid && fromSharerValid && sharerValid && nameValid

      // Build per-tier error map (needed for field-level display regardless of
      // whether the child ref is available)
      for (var i = 0; i < tiers.length; i++) {
        var t = tiers[i]
        var tierError = {}

        // Tier 1 must start at month 0
        if (i === 0 && t.fromMonth !== 0) {
          tierError.fromMonth = 'Tier 1 must start at month 0.'
        }

        // toMonth > fromMonth
        if (t.toMonth <= t.fromMonth) {
          tierError.range = 'Tier end must be greater than tier start (month ' + t.fromMonth + ').'
        }

        // Rate #effectiveFromSharer..toSharer must all be > 0 (completeness).
        // XOL-486: narrowed from a fixed 1..limit — positions below
        // effectiveFromSharer are hidden (not rendered) whenever Min >= 2, on
        // any rate (Min is unconditional now), so requiring them would name a
        // Rate # the user cannot see or fill in. The invariant this loop
        // preserves: the enumerated positions are always a SUBSET of the
        // rendered positions (SharerRateTierCard's ratePositions, threaded
        // the same effectiveFromSharer down as :from-sharer).
        var missing = []
        for (var p = this.effectiveFromSharer; p <= limit; p++) {
          if (!(Number(t['telco' + p]) > 0)) missing.push(p)
        }
        if (missing.length > 0) {
          tierError.completeness = 'Rate #' + missing.join(', #') + ' required (must be greater than 0).'
        }

        errors.push(tierError)
      }

      // Delegate the authoritative boolean to the child's validate() (which uses
      // the reactive isValid computed — includes contiguity). Fall back to local
      // check when the child ref is not yet mounted (e.g. unit tests).
      var valid
      if (this.$refs && this.$refs.tiersEditor && typeof this.$refs.tiersEditor.validate === 'function') {
        valid = this.$refs.tiersEditor.validate() && headerValid
      } else {
        // Local fallback: field-level errors + contiguity + header range checks
        var hasFieldErrors = errors.some(function (e) { return Object.keys(e).length > 0 })
        var contiguous = true
        for (var k = 1; k < tiers.length; k++) {
          if (tiers[k].fromMonth !== tiers[k - 1].toMonth) { contiguous = false; break }
        }
        valid = !hasFieldErrors && contiguous && headerValid
      }

      return { valid: valid, errors: errors }
    }
  },

  data () {
    return {
      guts: 12,
      row: this.rowData ? this.rowData : this.getTableStruct('rates'),
      tierValidationErrors: [],
      toSharerError: null,
      fromSharerError: null,
      // XOL-498: { field: 'from'|'to', text } naming the field the Min/Max
      // coupling moved, or null. Never set by anything but onMinCommitted /
      // onMaxCommitted, and cleared on every drawer open.
      bandFollowNote: null,
      // XOL-487: Sharer + Name required-field errors, set by validateTiers().
      sharerError: null,
      nameError: null,

      // XOL-497 Step 7 — the Min this drawer OPENED with, so a change can be
      // detected client-side. Snapshotting a prop-derived value in data() is
      // correct here and is NOT the "async store values belong in computed"
      // trap: `row` comes from a prop, not a bootstrap XHR, and RatesAction
      // keys this component per rate so a fresh instance re-runs created().
      // Set in created() AFTER normalizeHeader(), so the baseline is the
      // normalized number rather than whatever string the payload carried.
      initialFromSharer: null,

      // XOL-497 Step 8 — publish state. `hasPublishedVersion` defaults false
      // and is only ever set true by a SUCCESSFUL fetch that finds a current
      // version. A missing key, an empty list, or a rejected request all
      // leave it false: the badge must never claim "published" on evidence it
      // does not have.
      publishLoading: false,
      publishLoaded: false,
      publishErrored: false,
      hasPublishedVersion: false,
      // version_no of the is_current entry, purely so the badge can say which
      // version is live and agree with the Manage-versions drawer. Null is a
      // supported state: the caption falls back to version-free wording rather
      // than rendering "v null".
      publishedVersionNo: null
    }
  },

  created () {
    this.normalizeHeader()
    this.normalizeTiers()
    // XOL-497 Step 7 — must run after normalizeHeader().
    this.initialFromSharer = Number(this.row.fromSharer)
    this.loadPublishState()
    // For new records only: try to pre-seed tiers from the Default Tier Template.
    // Silent on GET failure — the pristine blank Tier 1 is already in place.
    // Existing rows (row.id set) are never affected.
    if (!this.row || !this.row.id) {
      this.fetchData('/rates/sharer/default-tiers', this.onTemplateSeed)
    }
  }

}
</script>

<style scoped>
/* XOL-462 §6.3: block-level wrapper forces a line break inside the inline
   form (el-form-item is display:inline-block) without a heavy <hr>. Element
   2.11 applies inline-form styling via the descendant selector
   `.el-form--inline .el-form-item`, which still matches through this wrapper. */
/* XOL-497: heading + publish badge on one line, wrapping to two on narrow
   widths rather than truncating. A half-rendered publish-state string is
   worse than one that wraps. Mirrors RateVersionHistory's .rvh-summary. */
.rate-settings__heading {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin: 12px 0 10px;
}

.rate-settings__title {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
}

/* min-height reserves the caption's line box so the header does not jump when
   the fetch settles — the loading state renders no text, and a collapsing
   empty <p> would shift everything below it. Matches RateVersionManager's
   .rvm-caption values; a separate class because scoped styles do not cross
   components. */
/* flex-basis: 100% because this now sits INSIDE .rate-settings__heading (the
   live region), so it is a flex item and would otherwise sit beside the badge.
   min-height reserves its line box so the header does not jump when the fetch
   settles — the loading state renders no text. */
.rate-settings__publish-caption {
  flex-basis: 100%;
  min-height: 17px;
  margin: 0;
  font-size: 12px;
  color: #909399;
}


/* XOL-498: the Min/Max follow cue. Same 12px / #909399 as the publish caption
   above — the form's existing quiet grey, not the error red, because this
   reports something the form DID rather than something the user must fix.

   The negative top margin is doing real work, not micro-tuning. This note is a
   SIBLING of the el-form-item holding the input (for Max, number-input renders
   that form-item itself and exposes no slot below it), so Element's
   `.el-form-item { margin-bottom: 22px }` sits between the two. Left at a
   positive offset the cue floats ~26px under its input — far enough that it
   reads as unrelated to the field it is explaining, which Carlos flagged on
   the first render. -18px lands it ~4px under the input, where a caption
   belongs.

   The two sibling error lines above have the same 22px gap and are
   deliberately NOT changed here: they are red, rare, and already screenshotted
   in XOL-490/493. Tightening them is a separate decision. */
.band-follow-note {
  font-size: 12px;
  color: #909399;
  margin-top: -18px;
}


.rate-settings__strict {
  display: block;
  margin-top: 8px;
}

.rate-settings__strict-title {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

/* XOL-487: Sharer/Name sit as bare children of an inline el-form, so
   el-form-item's error message (position: absolute; top: 100%) renders in
   the item's own bottom margin without reflowing anything else in the row —
   but only if it stays on one line. Pin a minimum width so the longest
   approved copy ("Name must be 48 characters or fewer.") never wraps. */
.rate-settings__named-field {
  min-width: 240px;
}
</style>
